import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SEO from '../../components/SEO';


const GOOGLE_CLIENT_ID = "957736829829-ebpu5bvvadglra8ujbjb8sb384ou4k27.apps.googleusercontent.com";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: unknown) => void;
          renderButton: (el: HTMLElement, opts: unknown) => void;
          prompt: () => void;
        };
      };
    };
  }
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Hidden container where Google renders its real button — we forward
  // clicks from our own styled "Continue with Google" button into it.
  const googleBtnRef = useRef<HTMLDivElement>(null);

  const handleCredentialResponse = useCallback(async (response: { credential?: string }) => {
    if (!response?.credential) return;
    setGoogleLoading(true);
    setError('');
    try {
      const isNew = await loginWithGoogle(response.credential);
      navigate(isNew ? '/choose-plan' : '/dashboard', { replace: true });
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('<!DOCTYPE') || msg.includes('not valid JSON') || msg.includes('Unexpected token')) {
        setError('The server is busy. Please wait a moment and try again.');
      } else {
        setError(msg || 'Google sign-in failed. Please try again.');
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [loginWithGoogle, navigate]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const initializeGoogle = () => {
      if (window.google && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 400,
        });
      }
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.body.appendChild(script);
    } else {
      initializeGoogle();
    }
  }, [handleCredentialResponse]);

  
  // const handleGoogleButtonClick = () => {
  //   const tryClick = (attempts: number) => {
  //     const realButton = googleBtnRef.current?.querySelector('div[role="button"]') as HTMLElement | null;
  //     if (realButton) {
  //       realButton.click();
  //       return;
  //     }
  //     if (window.google) {
  //       window.google.accounts.id.prompt();
  //       return;
  //     }
  //     if (attempts > 0) {
  //       setTimeout(() => tryClick(attempts - 1), 500);
  //     }
  //   };
  //   tryClick(10);
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      if (err.message?.includes('not verified') || err.message?.includes('verification')) {
        navigate('/verify-otp', { state: { email }, replace: true });
        return;
      }
      // HTML response means server is restarting or temporarily unavailable
      const msg = err.message || '';
      if (msg.includes('<!DOCTYPE') || msg.includes('not valid JSON') || msg.includes('Unexpected token')) {
        setError('The server is busy. Please wait a moment and try again.');
      } else {
        setError(msg || 'Invalid email or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <SEO title="Login" noIndex />
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[42%] bg-hero flex-col p-12 relative overflow-hidden">
        <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-blue-400/8 rounded-full blur-3xl pointer-events-none" />

        <Link to="/" className="flex items-center gap-2.5 relative z-10 mb-8">
          {/* <img src="/manna-plag-logo.png" alt="Manna Plag" style={{ height: 36, objectFit: "contain" }} /> */}
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "4px",
              padding: "6px 10px",
              boxShadow: "0 0 10px rgba(255,255,255,0.25)",
              animation: "borderBlink 2s ease-in-out infinite",
            }}
          >
            <img
              src="/manna-plag-logo-horizontal.png?v=2"
              alt="Manna Plag"
              style={{
                height: 28,
                objectFit: "contain",
              }}
            />
          </div>
        </Link>

        <div className="relative z-10 flex-1">
          <h2 className="text-[34px] font-black text-white leading-tight tracking-tight mb-4">Welcome back</h2>
          <p className="text-[14px] text-white/60 leading-relaxed mb-10 font-medium max-w-sm">
            Sign in to your Manna Plag account to access your reports, submissions, and settings.
          </p>
          <div className="flex flex-col gap-5">
            {[
              'Access all your past reports',
              'Submit new manuscripts instantly',
              'AI detection results saved',
              'Your data is secure and private',
            ].map(f => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-blue-500/25 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-black text-blue-300">✓</span>
                </div>
                <span className="text-[13px] text-white/75 font-medium">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 mt-8">
          <p className="text-[11px] text-white/25 font-medium">© {new Date().getFullYear()} Manna Analytics Private Limited</p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-slate-100">
        <div
          className="w-full max-w-[430px] bg-white rounded-xl border border-gray-100 shadow-sm p-8">
          <Link to="/" className="flex items-center gap-2 mb-4 lg:hidden">
            <img src="/manna-plag-logo-horizontal.png?v=2" alt="Manna Plag" style={{ height: 28, objectFit: "contain" }} />
          </Link>

          <h1 className="text-[26px] font-black text-gray-900 tracking-tight mb-1">Sign in</h1>
          <p className="text-[13px] text-gray-500 font-medium mb-7">
            Don't have an account?{' '}
            <Link to="/register" className="text-navy font-bold hover:underline">Create one free</Link>
          </p>

          <div style={{ position: 'relative', width: '100%', height: 44, marginBottom: 16 }}>
            <div style={{
              position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
              border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff',
              fontSize: 13, fontWeight: 600, color: '#374151',
            }}>
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" />
                <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
              {googleLoading ? 'Signing in...' : 'Continue with Google'}
            </div>
            <div ref={googleBtnRef} style={{
              position: 'absolute', inset: 0, zIndex: 2,
              overflow: 'hidden', opacity: 0.01, borderRadius: 12,
            }} />
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">or</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <input type="email" placeholder="Email address"
              value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all bg-white" />

            <div className="relative">
              <input type={showPass ? 'text' : 'password'} placeholder="Password"
                value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all bg-white pr-10" />
              <button type="button" onClick={() => setShowPass(!showPass)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showPass
                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>
                  }
                </svg>
              </button>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-[12px] text-navy font-semibold hover:underline">Forgot password?</Link>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-[12px] text-red-600 font-medium">{error}</p>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-navy text-white text-[13px] font-black rounded-md border border-navy hover:bg-white hover:text-navy hover:border-navy transition-all duration-200 disabled:opacity-60 uppercase tracking-tight mt-1"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="text-[11px] text-gray-400 text-center mt-6 font-medium">
            By signing in you agree to our{' '}
            <Link to="/terms" className="hover:underline">Terms</Link> and{' '}
            <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
